import { IFormData, IFormItem } from '~/store/slices/formSlice';
import { IInvoiceSerializer } from '~/types/invoice';

/**
 * Safely parse a numeric string to float, handling commas, currency symbols,
 * and other non-numeric characters.
 *
 * Examples:
 *   safeFloat("$1,000.50") -> 1000.5
 *   safeFloat("1,234.56") -> 1234.56
 *   safeFloat("€500")     -> 500
 *   safeFloat("")          -> 0
 *   safeFloat("invalid")  -> 0
 */
export const safeFloat = (value: string | number | null | undefined, fallback = 0): number => {
  if (value == null) return fallback;
  if (typeof value === 'number') return isNaN(value) ? fallback : value;

  const trimmed = String(value).trim();
  if (!trimmed) return fallback;

  // Strip everything except digits, decimal point, and leading minus
  let cleaned = trimmed.replace(/[^\d.\-]/g, '');

  // Handle multiple decimal points — keep only the first
  const dotIdx = cleaned.indexOf('.');
  if (dotIdx !== -1) {
    cleaned = cleaned.slice(0, dotIdx + 1) + cleaned.slice(dotIdx + 1).replace(/\./g, '');
  }

  const result = parseFloat(cleaned);
  return isNaN(result) ? fallback : result;
};

export const getAmount = (itemData: IFormItem) => {
  const qty = safeFloat(itemData.quantity);
  const price = safeFloat(itemData.price);
  return qty * price;
};

export const getTotalAmountDetails = (formData: IFormData) => {
  let subTotal = 0;
  let discount = safeFloat(formData.discount);

  // Sub Total
  for (const eachItem of formData.items) {
    subTotal += getAmount(eachItem);
  }

  // Discount
  if (formData.discountType === 'percent') {
    discount = (subTotal * discount) / 100;
  }

  const totalAfterDiscount = subTotal - discount;

  // Tax
  let tax = safeFloat(formData.tax);
  if (formData.taxType === 'percent') {
    tax = (totalAfterDiscount * tax) / 100;
  }

  const totalAfterTax = totalAfterDiscount + tax;

  // Shipping
  let shipping = safeFloat(formData.shipping);
  if (formData.shippingType === 'percent') {
    shipping = (totalAfterTax * shipping) / 100;
  }

  const totalAmount = totalAfterTax + shipping;

  return { subTotal, discount, tax, shipping, totalAmount };
};

export const serializeInvoiceData = (data: IInvoiceSerializer): IFormData => {
  const invoiceItems = [];
  const rawItems = data.items;
  for (let index = 0; index < rawItems.length; index++) {
    const invoiceItem = rawItems[index];
    invoiceItems.push({
      id: invoiceItem.id,
      description: invoiceItem.description,
      sku: invoiceItem.sku,
      quantity: invoiceItem.quantity.toString(),
      price: invoiceItem.price.toString(),
    });
  }

  return {
    invoiceUuid: data.invoice_uuid,
    invoiceNumber: data.invoice_number,
    logoUrl: data.logo_url,
    invoiceDate: data.invoice_date,
    dueDate: data.due_date,
    poNumber: data.po_number,
    notes: data.notes,
    terms: data.terms,
    currency: data.currency,
    discount:
      data.discount_type === 'percent'
        ? data.discount_percentage.toString()
        : data.discount_amount.toString(),
    discountType: data.discount_type,
    shipping:
      data.shipping_type === 'percent'
        ? data.shipping_percentage.toString()
        : data.shipping_amount.toString(),
    shippingType: data.shipping_type,
    tax: data.tax_type === 'percent' ? data.tax_percentage.toString() : data.tax_amount.toString(),
    taxType: data.tax_type,
    sellerDetails: {
      companyName: data.seller_company_name,
      address_line_1: data.seller_address_line_1,
      address_line_2: data.seller_address_line_2,
      city: data.seller_city,
      state: data.seller_state,
      postal_code: data.seller_postal_code,
      country: data.seller_country,
      email: data.seller_email,
      phone: data.seller_phone,
      website: data.seller_website,
    },
    buyerDetails: {
      companyName: data.buyer_company_name,
      address_line_1: data.buyer_address_line_1,
      address_line_2: data.buyer_address_line_2,
      city: data.buyer_city,
      state: data.buyer_state,
      postal_code: data.buyer_postal_code,
      country: data.buyer_country,
      email: data.buyer_email,
      phone: data.buyer_phone,
      website: data.buyer_website,
    },
    shippingDetails: {
      companyName: data.shipping_company_name,
      address_line_1: data.shipping_address_line_1,
      address_line_2: data.shipping_address_line_2,
      city: data.shipping_city,
      state: data.shipping_state,
      postal_code: data.shipping_postal_code,
      country: data.shipping_country,
      email: data.shipping_email,
      phone: data.shipping_phone,
      website: data.shipping_website,
    },
    template_id: data.template_id,
    isShippingUnique: data.is_shipping_unique,
    logo: null,
    items: invoiceItems,
    client_uuid: data.client_uuid,
    isShippingEnabled: data.is_shipping_enabled,
    saveTermsAsDefault: false,
    saveNotesAsDefault: false,
  };
};
